import mixpanel from 'mixpanel-browser';
import {
  ATTRIBUTION_PARAM_KEYS,
  getAttributionParams,
  getBackendAttribution,
  readStoredAttribution
} from './attribution';

// Google Tag Manager tracking utilities
export const gtmTrack = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventData
    });
  }
};

// Facebook Pixel tracking utilities
export const fbTrack = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, eventData);
  }
};

export const fbTrackCustom = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', eventName, eventData);
  }
};

export const PAID_SIGNUP_TRACKED_STORAGE_KEY = 'qlearn_paid_signup_tracked';

const safeReadPaidSignupMarkers = () => {
  try {
    const rawValue = window.localStorage.getItem(PAID_SIGNUP_TRACKED_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : {};
  } catch (_) {
    return {};
  }
};

const safeWritePaidSignupMarkers = (markers) => {
  try {
    window.localStorage.setItem(PAID_SIGNUP_TRACKED_STORAGE_KEY, JSON.stringify(markers));
  } catch (_) {
    // Conversion dedupe should not block login.
  }
};

const buildPaidSignupMarkerKey = ({ userData, storedAttribution, paidSource, paidCampaign, paidFbclid, paidGclid }) => [
  userData?.id || 'unknown-user',
  storedAttribution?.firstTouch?.capturedAt || storedAttribution?.latestTouch?.capturedAt || 'unknown-touch',
  paidSource || '',
  paidCampaign || '',
  paidFbclid || '',
  paidGclid || ''
].join('|');

const hasTrackedPaidSignup = (markerKey) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  return !!safeReadPaidSignupMarkers()[markerKey];
};

const markPaidSignupTracked = (markerKey) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  const markers = safeReadPaidSignupMarkers();
  markers[markerKey] = new Date().toISOString();
  safeWritePaidSignupMarkers(markers);
};

export const trackLandingPageView = (storedAttribution = readStoredAttribution()) => {
  const touch = storedAttribution?.latestTouch || storedAttribution?.firstTouch;
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : undefined;

  gtmTrack('landing_page_viewed', {
    landing_path: touch?.landingPath || currentPath,
    referrer: touch?.referrer,
    ...getAttributionParams(storedAttribution)
  });
};

export const trackDiagnosticStarted = ({
  quizId,
  userId,
  programCode,
  pgyLevel,
  storedAttribution = readStoredAttribution()
} = {}) => {
  gtmTrack('absite_diagnostic_started', {
    diagnostic_type: 'absite_readiness',
    quiz_id: quizId,
    user_id: userId,
    program_code: programCode,
    pgy_level: pgyLevel,
    ...getAttributionParams(storedAttribution)
  });
};

export const trackDiagnosticReportViewed = ({
  quizId,
  completed,
  attemptCount,
  weakestAreasCount
} = {}) => {
  gtmTrack('absite_diagnostic_report_viewed', {
    diagnostic_type: 'absite_readiness',
    quiz_id: quizId,
    completed,
    attempt_count: attemptCount,
    weakest_areas_count: weakestAreasCount
  });
};

export const trackDiagnosticProfileCaptured = ({
  quizId,
  programCode,
  pgyLevel
} = {}) => {
  gtmTrack('absite_diagnostic_profile_saved', {
    diagnostic_type: 'absite_readiness',
    quiz_id: quizId,
    program_code: programCode,
    pgy_level: pgyLevel
  });
};

export const trackProgramSummaryViewed = ({
  programCount,
  startedCount,
  completedCount
} = {}) => {
  gtmTrack('absite_program_summary_viewed', {
    diagnostic_type: 'absite_readiness',
    program_count: programCount,
    started_count: startedCount,
    completed_count: completedCount
  });
};

export const buildQLinkQuestionAnsweredMarkerKey = ({
  qlinkId,
  questionId,
  selectedAnswerKey
} = {}) => [
  qlinkId || 'unknown-link',
  questionId || 'unknown-question',
  selectedAnswerKey || 'unknown-answer'
].join('|');

// QLink answer tracking intentionally uses explicit allowlists for both GTM
// and backend payloads: ATTRIBUTION_PARAM_KEYS feeds snake_case GTM params,
// while QLINK_ANSWER_BACKEND_ATTRIBUTION_KEYS feeds camelCase backend fields.
// Keep additions in sync when a new safe attribution field is needed.
const QLINK_ANSWER_BACKEND_ATTRIBUTION_KEYS = [
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
  'gclid',
  'fbclid',
  'ref',
  'platform',
  'landingPath',
  'referrer',
  'capturedAt',
  'firstCapturedAt'
];

const stripSensitiveUrlParts = (value) => {
  if (!value) {
    return value;
  }

  const normalized = `${value}`;

  try {
    const parsed = new URL(normalized, 'https://qlearn.medlearnity.com');
    if (normalized.startsWith('/') && !normalized.startsWith('//')) {
      return parsed.pathname;
    }

    return `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
  } catch (_) {
    return normalized.split(/[?#]/)[0];
  }
};

const getQLinkAnswerAttributionParams = (storedAttribution) => {
  const params = getAttributionParams(storedAttribution);

  return ATTRIBUTION_PARAM_KEYS.reduce((acc, key) => {
    if (params?.[key]) {
      acc[key] = params[key];
    }
    return acc;
  }, {});
};

const pickQLinkAnswerBackendAttribution = (attribution = {}) => (
  QLINK_ANSWER_BACKEND_ATTRIBUTION_KEYS.reduce((acc, key) => {
    if (attribution?.[key]) {
      acc[key] = attribution[key];
    }
    return acc;
  }, {})
);

const sanitizeQLinkAnswerUrlAttribution = (attribution) => {
  if (!attribution) {
    return undefined;
  }

  return {
    ...attribution,
    landingPath: stripSensitiveUrlParts(attribution.landingPath),
    referrer: stripSensitiveUrlParts(attribution.referrer)
  };
};

const sanitizeQLinkAnswerAttribution = (attribution) => {
  if (!attribution) {
    return undefined;
  }

  const safeAttribution = sanitizeQLinkAnswerUrlAttribution(attribution);
  const pickedAttribution = pickQLinkAnswerBackendAttribution(safeAttribution);

  return Object.keys(pickedAttribution).length ? pickedAttribution : undefined;
};

export const buildQLinkQuestionAnsweredPayload = ({
  qlinkId,
  questionId,
  userId,
  platform,
  correct,
  storedAttribution = readStoredAttribution()
} = {}) => {
  const touch = storedAttribution?.firstTouch || storedAttribution?.latestTouch;

  return {
    qlink_id: qlinkId,
    question_id: questionId,
    user_id: userId,
    platform,
    answer_selected: true,
    is_correct: correct,
    ...getQLinkAnswerAttributionParams(storedAttribution),
    landing_path: stripSensitiveUrlParts(touch?.landingPath),
    referrer: stripSensitiveUrlParts(touch?.referrer)
  };
};

export const trackQLinkQuestionAnswered = (params = {}) => {
  gtmTrack('qlink_question_answered', buildQLinkQuestionAnsweredPayload(params));
};

export const buildQLinkQuestionAnsweredBackendPayload = ({
  qlinkId,
  questionId,
  userId,
  platform,
  correct,
  storedAttribution = readStoredAttribution()
} = {}) => ({
  qlinkId,
  questionId,
  userId,
  platform,
  correct,
  attribution: sanitizeQLinkAnswerAttribution(getBackendAttribution(storedAttribution))
});

// Combined tracking function for user sign-ins
export const trackUserSignIn = (userData, loginMethod = 'email') => {
  const trackingData = {
    user_id: userData.id,
    email: userData.email,
    name: userData.displayName,
    role: userData.primaryRole,
    login_method: loginMethod,
    event_category: 'Authentication',
    event_label: `${loginMethod}_login`,
    value: 1
  };

  // Track in Google Tag Manager
  gtmTrack('user_sign_in', {
    ...trackingData,
    custom_event_name: 'User Sign In'
  });

  // Track in Facebook Pixel as Lead event (valuable for ad optimization)
  fbTrack('Lead', {
    content_name: 'User Sign In',
    content_category: 'Authentication',
    value: 1,
    currency: 'USD'
  });

  // Track as custom Facebook event for more detailed analytics
  fbTrack('CompleteRegistration', {
    content_name: 'User Sign In',
    status: 'completed'
  });

  console.log('🔥 User sign-in tracked:', {
    gtm: true,
    facebook: true,
    method: loginMethod,
    userId: userData.id
  });
};

// Track user registration/first-time sign-ups
export const trackUserRegistration = (userData, registrationMethod = 'email') => {
  const trackingData = {
    user_id: userData.id,
    email: userData.email,
    name: userData.displayName,
    role: userData.primaryRole,
    registration_method: registrationMethod,
    event_category: 'Registration',
    event_label: `${registrationMethod}_registration`,
    value: 5
  };

  // Track in Google Tag Manager
  gtmTrack('user_registration', {
    ...trackingData,
    custom_event_name: 'User Registration'
  });

  // Track in Facebook Pixel as CompleteRegistration event
  fbTrack('CompleteRegistration', {
    content_name: 'User Registration',
    content_category: 'Registration',
    value: 5,
    currency: 'USD'
  });

  console.log('🎯 User registration tracked:', {
    gtm: true,
    facebook: true,
    method: registrationMethod,
    userId: userData.id
  });
};

// Track conversion events from ads (when users come from ad campaigns)
export const trackAdConversion = (userData, adSource = 'unknown') => {
  // Get URL parameters to detect ad campaigns
  const urlParams = new URLSearchParams(window.location.search);
  // Only rely on actual UTM parameters / click-IDs to decide whether this session
  // originated from a paid advertisement. DO NOT fall back to the internal
  // `adSource` argument here, otherwise every sign-in would be treated as an
  // ad conversion. See PR-239 bug-fix discussion.
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  const fbclid = urlParams.get('fbclid'); // Facebook click ID
  const gclid = urlParams.get('gclid'); // Google click ID
  const storedAttribution = readStoredAttribution();
  const storedParams = getAttributionParams(storedAttribution);
  const paidSource = utmSource || storedParams.utm_source;
  const paidMedium = utmMedium || storedParams.utm_medium;
  const paidCampaign = utmCampaign || storedParams.utm_campaign;
  const paidFbclid = fbclid || storedParams.fbclid;
  const paidGclid = gclid || storedParams.gclid;

  // The conversion event should fire ONLY when the user actually arrives with
  // identifiable ad parameters (utm_source/fbclid/gclid). This keeps organic
  // traffic from polluting paid ad metrics.
  if (paidSource || paidFbclid || paidGclid) {
    const markerKey = buildPaidSignupMarkerKey({
      userData,
      storedAttribution,
      paidSource,
      paidCampaign,
      paidFbclid,
      paidGclid
    });

    if (hasTrackedPaidSignup(markerKey)) {
      return;
    }

    const conversionData = {
      user_id: userData.id,
      utm_source: paidSource,
      utm_medium: paidMedium,
      utm_campaign: paidCampaign,
      facebook_click_id: paidFbclid,
      google_click_id: paidGclid,
      landing_path: storedAttribution?.firstTouch?.landingPath,
      referrer: storedAttribution?.firstTouch?.referrer,
      ad_source: adSource, // e.g. email_login or google_oauth (for internal analysis)
      event_category: 'Paid Click Signup',
      event_label: 'sign_in_from_ad',
      value: 1
    };

    // Track in GTM
    gtmTrack('paid_click_signup', {
      ...conversionData,
      custom_event_name: 'Paid Click Signup'
    });

    // Keep this as a custom signup signal; the normal sign-in path already
    // emits the generic Facebook Lead event.
    fbTrackCustom('PaidClickSignup', {
      content_name: 'Paid Click Sign In',
      content_category: 'Paid Click Signup',
      value: 1,
      currency: 'USD'
    });
    markPaidSignupTracked(markerKey);

    console.log('🚀 Ad conversion tracked:', {
      gtm: true,
      facebook: true,
      source: paidSource,
      userId: userData.id
    });
  }
};

// Phone + OTP Authentication Tracking
export const trackPhoneAuthEvent = (eventName, userData = {}, additionalData = {}) => {
  const trackingData = {
    user_id: userData?.id,
    phone_number: userData?.phoneNumber ? `***${userData.phoneNumber.slice(-4)}` : undefined, // Masked for privacy
    auth_method: 'phone_otp',
    event_category: 'Phone Authentication',
    event_label: eventName,
    ...additionalData
  };

  gtmTrack('phone_auth_event', trackingData);

  // Track significant events in Facebook Pixel
  if (eventName === 'otp_verified') {
    fbTrack('CompleteRegistration', {
      content_name: 'Phone Auth Success',
      value: 5,
      currency: 'USD'
    });
  }

  console.log('📱 Phone auth event tracked:', {
    event: eventName,
    userId: userData?.id
  });
};

// Referral funnel tracking — event names are queried by the growth
// dashboard, so keep them stable: referral_modal_opened,
// referral_link_copied, referral_share_clicked, referral_invite_landing_viewed.
export const trackReferralFunnel = (eventName, eventData = {}) => {
  gtmTrack(eventName, {
    event_category: 'Referral',
    ...eventData
  });

  try {
    mixpanel.track(eventName, eventData);
  } catch (trackingError) {
    // Tracking must never break the referral flow (e.g. mixpanel not initialized).
    console.debug('Referral tracking skipped:', trackingError?.message);
  }
};

// Paywall entry-point funnel — the upsell surfaces that lead into the
// subscriptions paywall previously fired ZERO analytics (premium-UX §6 gap).
// Dual-sink (GTM + Mixpanel) mirroring trackReferralFunnel so the growth
// dashboard can query a stable funnel. Event names are stable keys:
//   paywall_entry_clicked  — user clicked an upgrade CTA (source names the CTA)
//   paywall_entry_shown    — a display-only upsell surfaced (impression)
// eventData carries { source } identifying which paywall entry point fired.
export const trackPaywallEntry = (eventName, eventData = {}) => {
  gtmTrack(eventName, {
    event_category: 'Paywall',
    ...eventData
  });

  try {
    mixpanel.track(eventName, eventData);
  } catch (trackingError) {
    // Tracking must never break the paywall entry flow (e.g. mixpanel not initialized).
    console.debug('Paywall entry tracking skipped:', trackingError?.message);
  }
};

// Track specific phone auth stages
export const trackPhoneAuthStage = {
  started: () => trackPhoneAuthEvent('phone_auth_started', {}),

  otpSent: (phoneNumber, isNewUser) => trackPhoneAuthEvent('otp_sent', { phoneNumber }, {
    is_new_user: isNewUser
  }),

  otpResent: (phoneNumber) => trackPhoneAuthEvent('otp_resent', { phoneNumber }),

  otpVerified: (userData) => {
    trackPhoneAuthEvent('otp_verified', userData);
    trackUserSignIn(userData, 'phone_otp');
    trackAdConversion(userData, 'phone_auth');
  },

  otpFailed: (error) => trackPhoneAuthEvent('otp_failed', {}, { error }),

  profileCompleted: (userData) => trackPhoneAuthEvent('profile_completed', userData),

  authAbandoned: (stage) => trackPhoneAuthEvent('phone_auth_abandoned', {}, { stage })
};
